import { Injectable } from '@angular/core';

// Interfaces for better type safety
export interface VariableFilterOptions {
  [key: string]: string | number | boolean | VariableFilterObject;
}

export interface VariableFilterObject {
  [key: string]: string | number;
}

export enum SortOrder {
  ASC = 'asc',
  DESC = 'desc',
}

export interface VariableSortOptions {
  sortBy?: string;
  order?: SortOrder;
  page?: number;
  limit?: number;
}

export interface GetVariablesGloballyOptions {
  filter?: VariableFilterOptions;
  sort?: VariableSortOptions;
  page?: number;
  limit?: number;
}

export interface VariableQueryParams {
  filters: string;
  sort: string;
  page: string;
  limit: string;
}

// Generic query builder interface
export interface GenericQueryOptions {
  filter?: Record<string, any>;
  sort?: {
    sortBy?: string;
    order?: 'asc' | 'desc';
    page?: number;
    limit?: number;
  };
  baseFilters?: Record<string, any>;
}

@Injectable({
  providedIn: 'root',
})
export class DocumentQueryHelper {
  private readonly defaultSortOptions: Required<VariableSortOptions> = {
    sortBy: 'created_at',
    order: SortOrder.DESC,
    page: 1,
    limit: 10,
  };

  /**
   * Builds query parameters for variable retrieval
   */
  buildVariableQueryParams(options?: GetVariablesGloballyOptions): VariableQueryParams {
    // eslint-disable-next-line object-property-newline
    const sortOptions = { ...this.defaultSortOptions, ...options?.sort };

    return {
      filters: this.buildFilterString(options?.filter),
      sort: this.buildSortString(sortOptions),
      page: sortOptions.page.toString(),
      limit: sortOptions.limit.toString(),
    };
  }

  /**
   * Constructs the complete query string from parameters
   */
  constructQueryString(params: VariableQueryParams): string {
    const { filters, sort, page, limit } = params;

    return `?${filters}&sort=${sort}&page=${page}&limit=${limit}`;
  }

  /**
   * Builds complete query string from options (convenience method)
   */
  buildCompleteQueryString(options?: GetVariablesGloballyOptions): string {
    const queryParams = this.buildVariableQueryParams(options);

    return this.constructQueryString(queryParams);
  }

  /**
   * Generic query builder for different API endpoints with base filters support
   */
  buildGenericQueryString(options?: GenericQueryOptions): string {
    const sortOptions = {
      sortBy: 'created_at',
      order: 'desc' as const,
      page: 1,
      limit: 10,
      ...options?.sort,
    };

    // Build filters object starting with base filters
    const filtersObject: Record<string, any> = { ...options?.baseFilters };

    // Add additional filters from options
    if (options?.filter) {
      Object.entries(options.filter)
        .filter(([_, value]) => this.isValidFilterValue(value))
        .forEach(([key, value]) => {
          if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            filtersObject[key] = value;
          } else {
            filtersObject[key] = { regex: value };
          }
        });
    }

    const filtersString =
      Object.keys(filtersObject).length > 0 ? `?filters=${JSON.stringify(filtersObject)}` : '?filters={}';

    const sortString = `&sort=${JSON.stringify({ [sortOptions.sortBy]: sortOptions.order })}`;
    const pageString = `&page=${sortOptions.page}`;
    const limitString = `&limit=${sortOptions.limit}`;

    return `${filtersString}${sortString}${pageString}${limitString}`;
  }

  /**
   * Builds the filter string for the API query
   */
  private buildFilterString(filterOptions?: VariableFilterOptions): string {
    if (!filterOptions) {
      return 'filters={}';
    }

    const filterEntries = Object.entries(filterOptions)
      .filter(([_, value]) => this.isValidFilterValue(value))
      .map(([key, value]) => this.formatFilterEntry(key, value));

    const filtersContent = filterEntries.length > 0 ? filterEntries.join(',') : '';

    return `filters={${filtersContent}}`;
  }

  /**
   * Builds the sort string for the API query
   */
  private buildSortString(sortOptions: Required<VariableSortOptions>): string {
    return JSON.stringify({
      [sortOptions.sortBy]: sortOptions.order,
    });
  }

  /**
   * Checks if a filter value is valid (not null, undefined, or empty string)
   */
  private isValidFilterValue(value: unknown): boolean {
    return value !== null && value !== undefined && value !== '';
  }

  /**
   * Formats a single filter entry based on value type
   */
  private formatFilterEntry(key: string, value: string | number | boolean | VariableFilterObject): string {
    if (typeof value === 'object' && value !== null) {
      const nestedEntries = Object.entries(value)
        .map(([nestedKey, nestedValue]) => `"${nestedKey}":"${nestedValue}"`)
        .join(',');

      return `"${key}":{${nestedEntries}}`;
    }

    return `"${key}":{"regex":"${value}"}`;
  }
}
